import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Tag, Bookmark, FileCheck2, Banknote, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/Wordmark";
import { BrandMark } from "@/components/BrandMark";
import { DEMO_MEMBER } from "@/lib/mock-data";

// PUB-04c — Ceremonial post-onboarding welcome.
// Renders as a full-bleed standalone screen (no shell chrome) — a single
// moment between the gated onboarding flow and the member dashboard.
// On CTA click, a teal-tinted overlay fades in over ~600ms before we
// navigate to the dashboard so the transition feels intentional, not abrupt.
export default function WelcomePage() {
  const navigate = useNavigate();
  const firstName = DEMO_MEMBER?.firstName || "there";
  const [mounted, setMounted] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    // Trigger the staggered entrance animation on mount.
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  const handleContinue = () => {
    setLeaving(true);
    setTimeout(() => navigate("/member/dashboard", { replace: true }), 620);
  };

  return (
    <div data-testid="welcome-page" className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white">
      {/* Ambient depth layers — fixed CSS, no library deps */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        {/* Top-right teal glow */}
        <div className="absolute -top-40 -right-40 w-[680px] h-[680px] rounded-full"
             style={{ background: "radial-gradient(closest-side, rgba(94,234,212,0.25), transparent 70%)" }} />
        {/* Bottom-left brand glow */}
        <div className="absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full"
             style={{ background: "radial-gradient(closest-side, rgba(20,184,166,0.18), transparent 70%)" }} />
        {/* Grain */}
        <div className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
             style={{ backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' seed='4'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")" }} />
        {/* Sparkle dots — pure CSS, staggered twinkle */}
        {SPARKLES.map((s, i) => (
          <span
            key={i}
            className="absolute size-1 rounded-full bg-white/60"
            style={{
              top: s.top, left: s.left,
              animation: `welcome-twinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Top bar: wordmark left, version stamp right */}
      <header className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-9 flex items-center justify-between">
        <Link to="/" className="inline-flex text-white">
          <Wordmark size="md" tone="white" />
        </Link>
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
          Welcome · onboarding complete
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-20">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-start">
          {/* Left: Hero copy */}
          <div className={`transition-all duration-700 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm text-caption font-semibold uppercase tracking-wide">
              <span className="size-1.5 rounded-full bg-emerald-300 animate-pulse" />
              <Sparkles className="size-3" />
              <span>You&rsquo;re officially in</span>
            </div>

            <h1 className="font-brand mt-7 text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-[-0.025em]">
              Welcome to the club,
              <br />
              <span className="bg-gradient-to-r from-emerald-200 via-emerald-100 to-white bg-clip-text text-transparent">
                {firstName}.
              </span>
            </h1>

            <p className="mt-7 text-body sm:text-body-lg text-white/70 leading-relaxed max-w-xl">
              From paste-to-payout in four steps. Browse open deals, lock in your rate, submit your cards, ACH lands in your bank. That&rsquo;s the whole loop.
            </p>

            {/* CTA pair */}
            <div className={`mt-10 flex items-center gap-4 flex-wrap transition-all duration-700 delay-300 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              <Button
                onClick={handleContinue}
                disabled={leaving}
                data-testid="welcome-continue-btn"
                className="group relative h-12 pl-6 pr-1.5 rounded-full bg-white text-ink hover:bg-emerald-50 transition-all duration-300 shadow-[0_8px_28px_rgba(0,0,0,0.18)] hover:shadow-[0_12px_36px_rgba(0,0,0,0.25)] font-semibold"
              >
                <span>Enter your dashboard</span>
                <span className="ml-3 size-9 rounded-full bg-ink text-white grid place-items-center transition-transform group-hover:translate-x-0.5">
                  <ArrowRight className="size-4" strokeWidth={2.4} />
                </span>
              </Button>
              <Link to="/member/deals" className="text-body-sm font-semibold text-white/60 hover:text-white inline-flex items-center gap-1.5 transition-colors">
                Or browse deals first
                <ArrowRight className="size-3.5" strokeWidth={2.4} />
              </Link>
            </div>
          </div>

          {/* Right: 4-step lifecycle list (each step animates in) */}
          <ol className="space-y-2">
            {STEPS.map((s, i) => (
              <li
                key={i}
                className={`relative bg-white/[0.06] border border-white/10 rounded-xl px-5 py-4 backdrop-blur-sm transition-all duration-700 ${
                  mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
                style={{ transitionDelay: `${300 + i * 120}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="relative shrink-0">
                    <div className="size-10 rounded-full bg-white/10 border border-white/15 grid place-items-center font-brand font-bold text-body-sm tabular-nums">
                      {i + 1}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <div className="flex items-center gap-2 text-body font-semibold text-white">
                      <span className="text-emerald-300"><s.icon className="size-4" /></span>
                      {s.title}
                    </div>
                    <p className="mt-1 text-caption text-white/65 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </main>

      {/* Footer brand mark */}
      <footer className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pb-9 flex items-center justify-between">
        <BrandMark id="MEMBER" />
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
          Members only · est. 2026
        </span>
      </footer>

      {/* Leave overlay — fades the brand-teal panel out to white, then we navigate */}
      <div
        aria-hidden
        className={`pointer-events-none fixed inset-0 bg-surface z-30 transition-opacity duration-[620ms] ease-out ${
          leaving ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Keyframes — inlined so the file has no external animation deps */}
      <style>{`
        @keyframes welcome-twinkle {
          0%, 100% { opacity: 0.15; transform: scale(0.6); }
          50%      { opacity: 0.85; transform: scale(1.0); }
        }
      `}</style>
    </div>
  );
}

// Random sparkle positions — fixed list so layout is deterministic.
const SPARKLES = [
  { top: "12%", left: "8%",  dur: 3.4, delay: 0.0 },
  { top: "18%", left: "72%", dur: 4.1, delay: 0.6 },
  { top: "26%", left: "38%", dur: 3.7, delay: 1.2 },
  { top: "34%", left: "85%", dur: 4.6, delay: 0.3 },
  { top: "44%", left: "14%", dur: 3.9, delay: 1.5 },
  { top: "55%", left: "62%", dur: 4.2, delay: 0.9 },
  { top: "63%", left: "30%", dur: 3.5, delay: 0.4 },
  { top: "72%", left: "78%", dur: 4.0, delay: 1.7 },
  { top: "82%", left: "20%", dur: 3.8, delay: 1.0 },
  { top: "88%", left: "54%", dur: 4.4, delay: 0.2 },
];

const STEPS = [
  { icon: Tag,         title: "Browse open deals",
    body: "Each card on Browse Deals shows the rate, cap remaining, deadline, and accepted brands." },
  { icon: Bookmark,    title: "Reserve your share",
    body: "Locks in the rate. On multi-brand deals, pick which brand at reservation — each brand has its own cap." },
  { icon: FileCheck2,  title: "Submit your cards",
    body: "Paste card numbers and PINs before the deadline. We OCR-check each card against the deal's rules." },
  { icon: Banknote,    title: "ACH lands in your bank",
    body: "Approved cards pay out on the deal's terms (typically 5–10 days from approval)." },
];
